import { createSelector } from '@reduxjs/toolkit';
import { domain, vgsDomain, nodeDomain, pdfDomain } from 'api/utils';

const selectUser = (state) => state.user;

export const authTokenSelector = createSelector(
  selectUser,
  (userState) => userState.currentUser.data?.auth_token
);

// Both set by the store (app/store.js): importing the store here would be
// circular. unauthorizedHandler ends the session; tokenRefreshedHandler swaps
// in the auth_token a refresh produced, given the token it replaces so the
// store can refuse it when the login changed meanwhile (sign-out, login-as).
let unauthorizedHandler = () => {};
let tokenRefreshedHandler = () => {};

export const onUnauthorized = (handler) => {
  unauthorizedHandler = handler;
};

export const onTokenRefreshed = (handler) => {
  tokenRefreshedHandler = handler;
};

// The refresh session lives in an httpOnly cookie cp-api scopes to /v1/auth,
// so these two calls go straight to cp-api, never through VGS, and carry
// credentials. One refresh at a time: requests that 401 together share it.
let refreshInFlight = null;

// The newest auth_token a refresh produced, and the one it replaced. A request
// built from an older state snapshot can retry with it instead of rotating the
// refresh session again (cp-api revokes a refresh token reused outside its
// 10 s grace window, so needless rotations are not free).
let latestToken = null;
let replacedToken = null;

// Called when the session ends (sign-out, or a 401 no refresh could cure) so
// a late request from the old login cannot retry with that login's token once
// someone else has signed in.
export const forgetRefreshedTokens = () => {
  latestToken = null;
  replacedToken = null;
};

// Resolves to:
//   { token }          a new auth_token (also handed to the store)
//   { denied: true }   cp-api refused (401): the session is over
//   { failed: true }   cp-api unreachable, 5xx or a malformed body: the
//                      session is still alive, nothing is known
// staleToken is the auth_token the caller sent and got a 401 for.
export const refreshAccessToken = (staleToken) => {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${domain()}/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
      .then(async (res) => {
        if (res.status === 401) return { denied: true };
        if (res.status !== 200) return { failed: true };
        const json = await res.json();
        if (!json?.auth_token) return { failed: true };
        latestToken = json.auth_token;
        replacedToken = staleToken;
        tokenRefreshedHandler(json.auth_token, staleToken);
        return { token: json.auth_token };
      })
      .catch(() => ({ failed: true }))
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

// Revokes the refresh session behind the cookie and clears it. Works after the
// auth_token has expired; a failure only leaves a session the server will
// time out on its own.
export const endSession = () => {
  forgetRefreshedTokens();
  return fetch(`${domain()}/v1/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  }).catch(() => {});
};

export const createRequest = async (
  endpoint,
  state,
  { method, body, headers, contentType, isNode, vgs, isPdfRenderer }
) => {
  let reqDomain = domain();
  if (isNode) {
    reqDomain = nodeDomain();
  } else if (vgs) {
    reqDomain = vgsDomain();
  } else if (isPdfRenderer) {
    reqDomain = pdfDomain();
  }
  let url = `${reqDomain}${endpoint}`;
  const jwt = authTokenSelector(state);
  const isCpApi = !isNode && !isPdfRenderer;
  const isJSON = contentType !== 'multipart/form-data';
  const payload = !!body && isJSON ? JSON.stringify(body) : body;

  const buildOpts = (token) => {
    const opts = {
      method,
      headers: {
        'auth-token': token,
        ...headers,
      },
    };
    // Direct cp-api calls carry cookies so sign-in, password change and
    // logout can set or clear the refresh cookie. VGS is another site; the
    // cookie would not go there anyway.
    if (isCpApi && !vgs) {
      opts.credentials = 'include';
    }
    if (isJSON) {
      opts.headers['Content-Type'] = 'application/json';
    }
    if (!!payload) {
      opts.body = payload;
    }
    return opts;
  };

  let res = await fetch(url, buildOpts(jwt));
  if (res.status !== 401 || !jwt) {
    return res;
  }

  // An auth_token lasts 15 minutes, so a 401 on a request that carried one
  // first asks the refresh session for a new token and retries once. The node
  // service (QuickBooks) verifies the same token, so it gets the retry too.
  //
  // A request built from a state snapshot older than the last refresh carries
  // the token that refresh replaced: retry with the newer one before asking
  // for another. If that one is dead too (it has had its own 15 minutes), it
  // is the token to refresh from, not a verdict on the session.
  let sent = jwt;
  if (latestToken && jwt === replacedToken) {
    sent = latestToken;
    res = await fetch(url, buildOpts(sent));
    if (res.status !== 401) {
      return res;
    }
  }

  const outcome = await refreshAccessToken(sent);
  if (outcome.failed) {
    // Nothing is known about the session: let the caller report the error
    // and keep the login so the next request can try again. cp-api keeps
    // the cookie on its own 500 for the same reason.
    return res;
  }
  if (outcome.token) {
    sent = outcome.token;
    res = await fetch(url, buildOpts(sent));
    if (res.status !== 401) {
      return res;
    }
  }

  // cp-api (direct or through VGS) answers 401 only when the login is
  // missing, invalid or expired, so a 401 that survives the refresh means the
  // session is over. The node and PDF services' 401s don't end it.
  if (isCpApi) {
    forgetRefreshedTokens();
    res.sessionEnded = true;
    unauthorizedHandler(sent);
  }
  return res;
};
